import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { marked } from 'marked';
import { gfmHeadingId } from 'marked-gfm-heading-id';
import './RichTextEditor.scss';

interface RichTextEditorProps {
  initialValue: string;
  onChange: (value: string) => void;
}

interface RichTextEditorRef {
  getContent: () => string;
  setContent: (content: string) => void;
  focus: () => void;
}

export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>((props, ref) => {
  const { initialValue, onChange } = props;
  const editorRef = useRef<HTMLDivElement>(null);
  const [content, setContent] = useState(initialValue || '');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isMarkdownMode, setIsMarkdownMode] = useState(false);

  // Configure marked for GitHub Flavored Markdown
  marked.use(gfmHeadingId());
  marked.setOptions({
    gfm: true,
    breaks: true
  });

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    getContent: () => editorRef.current?.innerHTML || '',
    setContent: (content: string) => {
      if (editorRef.current) {
        editorRef.current.innerHTML = content;
        handleChange();
      }
    },
    focus: () => editorRef.current?.focus()
  }));

  // Initialize editor content when component mounts or initialValue changes
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialValue || '';
    }
  }, [initialValue]);

  // Handle content changes
  const handleChange = () => {
    if (!editorRef.current) return;
    const newContent = isMarkdownMode 
      ? editorRef.current.innerText 
      : editorRef.current.innerHTML;
    setContent(newContent);
    onChange(newContent);
  };

  // Basic text formatting functions
  const execCommand = (command: string, value: string = '') => {
    if (isMarkdownMode) {
      // Handle markdown formatting
      const selection = window.getSelection();
      if (!selection || !editorRef.current) return;

      const text = editorRef.current.innerText;
      const start = selection.anchorOffset;
      const end = selection.focusOffset;
      const selectedText = text.substring(start, end);

      let newText = '';
      switch (command) {
        case 'bold':
          newText = `**${selectedText}**`;
          break;
        case 'italic':
          newText = `*${selectedText}*`;
          break;
        case 'formatBlock':
          if (value === '<h2>') newText = `## ${selectedText}`;
          else if (value === '<h3>') newText = `### ${selectedText}`;
          else if (value === '<blockquote>') newText = `> ${selectedText}`;
          else newText = selectedText;
          break;
        case 'insertOrderedList':
          newText = `1. ${selectedText}`;
          break;
        case 'insertUnorderedList':
          newText = `- ${selectedText}`;
          break;
        case 'createLink':
          newText = `[${selectedText}](${value})`;
          break;
        case 'insertImage':
          newText = `![${selectedText || 'image'}](${value})`;
          break;
        default:
          newText = selectedText;
      }

      const beforeText = text.substring(0, start);
      const afterText = text.substring(end);
      editorRef.current.innerText = beforeText + newText + afterText;
    } else {
      // Regular rich text formatting
      document.execCommand(command, false, value);
    }
    editorRef.current?.focus();
    handleChange();
  };

  // Format commands
  const formatBold = () => execCommand('bold');
  const formatItalic = () => execCommand('italic');
  const formatUnderline = () => execCommand('underline');
  const formatHeading = (level: number) => {
    if (level >= 1 && level <= 6) {
      execCommand('formatBlock', `<h${level}>`);
    }
  };
  const formatParagraph = () => execCommand('formatBlock', '<p>');
  const formatBlockquote = () => execCommand('formatBlock', '<blockquote>');
  const formatOrderedList = () => execCommand('insertOrderedList');
  const formatUnorderedList = () => execCommand('insertUnorderedList');
  const formatLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };
  const formatImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
    }
  };
  const formatClear = () => execCommand('removeFormat');

  // Function to extract YouTube Video ID from various URL formats
  const getYoutubeVideoId = (url: string): string | null => {
    const regex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regex);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Function to format and insert YouTube video
  const formatYoutubeVideo = () => {
    const url = prompt('Enter YouTube Video URL:');
    if (!url) return;

    if (isMarkdownMode) {
      // In markdown mode, insert a link
      const selection = window.getSelection();
      if (!selection || !editorRef.current) return;

      const text = editorRef.current.innerText;
      const start = selection.anchorOffset;
      const end = selection.focusOffset;
      const selectedText = text.substring(start, end) || "YouTube Video";

      const beforeText = text.substring(0, start);
      const afterText = text.substring(end);
      editorRef.current.innerText = beforeText + `[${selectedText}](${url})` + afterText;
      handleChange();
    } else {
      // In rich text mode, insert iframe
      const videoId = getYoutubeVideoId(url);
      if (videoId) {
        const iframeHtml = `<iframe 
          width="560" 
          height="315" 
          src="https://www.youtube.com/embed/${videoId}" 
          title="YouTube video player" 
          frameborder="0" 
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
          allowfullscreen
        ></iframe>`;
        // Insert a paragraph before and after iframe for spacing
        execCommand('insertHTML', `<p><br></p>${iframeHtml}<p><br></p>`);
      } else {
        alert('Invalid YouTube URL provided.');
      }
    }
  };

  // Toggle between modes
  const toggleMarkdownMode = () => {
    if (!isMarkdownMode && editorRef.current) {
      // Convert HTML to Markdown when switching to markdown mode
      const htmlContent = editorRef.current.innerHTML;
      // You might want to use a HTML to Markdown converter here
      // For now, we'll just use the text content
      editorRef.current.innerText = htmlContent.replace(/<[^>]*>/g, '');
    }
    setIsMarkdownMode(!isMarkdownMode);
    setIsPreviewMode(false);
  };

  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  return (
    <div className="rich-text-editor">
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <button
            type="button"
            onClick={formatBold}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Bold (**text**)" : "Bold"}
          >
            <i className="fas fa-bold"></i>
          </button>
          <button
            type="button"
            onClick={formatItalic}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Italic (*text*)" : "Italic"}
          >
            <i className="fas fa-italic"></i>
          </button>
          <button
            type="button"
            onClick={formatUnderline}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title="Underline"
            disabled={isMarkdownMode}
          >
            <i className="fas fa-underline"></i>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => formatHeading(2)}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Heading 2 (## text)" : "Heading 2"}
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => formatHeading(3)}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Heading 3 (### text)" : "Heading 3"}
          >
            H3
          </button>
          <button
            type="button"
            onClick={formatParagraph}
            className="toolbar-button"
            title="Paragraph"
          >
            <i className="fas fa-paragraph"></i>
          </button>
          <button
            type="button"
            onClick={formatBlockquote}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Blockquote (> text)" : "Blockquote"}
          >
            <i className="fas fa-quote-right"></i>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={formatOrderedList}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Ordered List (1. text)" : "Ordered List"}
          >
            <i className="fas fa-list-ol"></i>
          </button>
          <button
            type="button"
            onClick={formatUnorderedList}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Unordered List (- text)" : "Unordered List"}
          >
            <i className="fas fa-list-ul"></i>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={formatLink}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Insert Link ([text](url))" : "Insert Link"}
          >
            <i className="fas fa-link"></i>
          </button>
          <button
            type="button"
            onClick={formatImage}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title={isMarkdownMode ? "Insert Image (![alt](url))" : "Insert Image"}
          >
            <i className="fas fa-image"></i>
          </button>
          <button
            type="button"
            onClick={formatYoutubeVideo}
            className={`toolbar-button ${isMarkdownMode ? 'markdown-mode' : ''}`}
            title="Insert YouTube Video"
          >
            <i className="fab fa-youtube"></i>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={formatClear}
            className="toolbar-button"
            title="Clear Formatting"
          >
            <i className="fas fa-eraser"></i>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={toggleMarkdownMode}
            className={`toolbar-button ${isMarkdownMode ? 'active' : ''}`}
            title="Toggle Markdown Mode"
          >
            <i className="fas fa-code"></i>
          </button>
          <button
            type="button"
            onClick={togglePreviewMode}
            className={`toolbar-button ${isPreviewMode ? 'active' : ''}`}
            title="Toggle Preview"
            disabled={!isMarkdownMode}
          >
            <i className="fas fa-eye"></i>
          </button>
        </div>
      </div>

      {isPreviewMode && isMarkdownMode ? (
        <div className="editor-preview">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content}
          </ReactMarkdown>
        </div>
      ) : (
        <div
          ref={editorRef}
          className={`editor-content ${isMarkdownMode ? 'markdown-mode' : ''}`}
          contentEditable={true}
          onInput={handleChange}
          onBlur={handleChange}
          dangerouslySetInnerHTML={{ __html: initialValue || '' }}
        ></div>
      )}
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor'; 